import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, FilePlus2, Loader2 } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import {
    BrainBankSyncSummary,
    ImportedTranscriptMeta,
} from "../../../../types";
import {
    getMeetingImportedTranscript,
    importMeetingTranscriptPdf,
} from "../../../../service/apiService";

interface ImportedTranscriptPanelProps {
    sessionId: string;
    // The meeting's imported transcript, if any — owned by the detail view so
    // the card header can show Delete/Download for it.
    importedMeta: ImportedTranscriptMeta | null;
    onImported: (meta: ImportedTranscriptMeta) => void;
}

const MAX_PDF_MB = 25;

// The user-facing reason an import failed, from the server when it gave one.
const importErrorMessage = (err: any): string => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (typeof detail?.message === "string" && detail.message) return detail.message;
    return "The PDF could not be imported. Try again or use a different file.";
};

const syncToast = (sync: BrainBankSyncSummary | undefined) => {
    if (!sync) return;
    if (sync.failed > 0) {
        toast.error(
            `${sync.failed} BrainBank cop${sync.failed === 1 ? "y" : "ies"} could not be updated. Send to BrainBank again to fix it.`
        );
    } else if (sync.synced > 0 || sync.removed > 0) {
        toast.success("BrainBank copies updated.");
    }
};

/**
 * The "Imported transcript" tab of the Transcription card (WS-03).
 *
 * Owns the import flow's states — empty (drop zone), importing, failed with
 * retry, and the transcript itself, shown exactly as it reads out of the PDF.
 * Deleting lives in the card header (the parent), next to Download.
 */
const ImportedTranscriptPanel: React.FC<ImportedTranscriptPanelProps> = ({
    sessionId,
    importedMeta,
    onImported,
}) => {
    const [text, setText] = useState<string | null>(null);
    const [textLoading, setTextLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importingName, setImportingName] = useState<string>("");
    const [importError, setImportError] = useState<string | null>(null);

    // Fetch the text whenever the meeting's import changes (including right
    // after an upload — imported_at moves, so a replaced file refetches too).
    useEffect(() => {
        let cancelled = false;
        if (!importedMeta) {
            setText(null);
            return;
        }
        setTextLoading(true);
        getMeetingImportedTranscript(sessionId)
            .then((imported) => {
                if (!cancelled) setText(imported.text);
            })
            .catch((err) => {
                console.error("Error loading imported transcript:", err);
                if (!cancelled) setText(null);
            })
            .finally(() => {
                if (!cancelled) setTextLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId, importedMeta?.imported_at]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDrop = useCallback(
        async (accepted: File[]) => {
            const file = accepted[0];
            if (!file) return;
            if (file.size > MAX_PDF_MB * 1024 * 1024) {
                setImportError(`That PDF is larger than ${MAX_PDF_MB} MB.`);
                return;
            }
            setImportError(null);
            setImporting(true);
            setImportingName(file.name);
            try {
                const result = await importMeetingTranscriptPdf(sessionId, file);
                toast.success(
                    result.replaced_previous
                        ? "Imported transcript replaced."
                        : "Transcript imported."
                );
                syncToast(result.brain_bank_sync);
                onImported(result.imported);
            } catch (err) {
                console.error("Error importing transcript PDF:", err);
                setImportError(importErrorMessage(err));
            } finally {
                setImporting(false);
            }
        },
        [sessionId, onImported]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { "application/pdf": [".pdf"] },
        maxFiles: 1,
        multiple: false,
        disabled: importing,
        onDrop: handleDrop,
        onDropRejected: () => setImportError("Only a single PDF file can be imported."),
    });

    // ---------------------------------------------------------------- states

    if (importing) {
        return (
            <div className="border-2 border-dashed border-violet-200 bg-violet-50/50 rounded-lg px-6 py-10 flex flex-col items-center text-center gap-3">
                <Loader2 className="w-9 h-9 text-violet-600 animate-spin" />
                <p className="text-sm font-semibold text-gray-700">
                    Importing <span className="font-mono">{importingName}</span>…
                </p>
                <p className="text-sm text-gray-500 max-w-md">
                    This usually takes under a minute. The transcript will appear
                    here when it's ready.
                </p>
            </div>
        );
    }

    if (importedMeta) {
        return (
            <div className="space-y-4">
                <p className="text-xs text-gray-400">
                    Shown as it appears in the PDF — formatting varies by source.
                </p>
                {textLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                    </div>
                ) : text !== null ? (
                    <p className="text-gray-700 whitespace-pre-wrap">{text}</p>
                ) : (
                    <p className="text-gray-500 text-center py-4">
                        The imported transcript could not be loaded. Reload the page
                        to try again.
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {importError && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-3.5 py-3">
                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{importError}</p>
                </div>
            )}
            <div
                {...getRootProps({
                    className: `border-2 border-dashed rounded-lg px-6 py-9 flex flex-col items-center text-center gap-3 cursor-pointer transition-colors ${
                        isDragActive
                            ? "border-violet-500 bg-violet-50"
                            : "border-gray-300 hover:border-violet-400"
                    }`,
                })}
            >
                <input {...getInputProps()} />
                <FilePlus2 className="h-11 w-11 text-gray-400" />
                <p className="text-sm font-semibold text-gray-700">
                    {importError ? "Try again" : "No imported transcript yet"}
                </p>
                {!importError && (
                    <p className="text-sm text-gray-500 max-w-md">
                        If you have a PDF transcript of this meeting — from Teams,
                        Zoom or anywhere else — add it here as a backup to the live
                        transcript.
                    </p>
                )}
                <p className="text-sm text-gray-700">
                    <span className="text-violet-600 font-semibold">
                        Upload transcript PDF
                    </span>{" "}
                    or drag and drop
                </p>
                <p className="text-xs text-gray-400">
                    PDF only · one per meeting · shown as it appears in the file
                </p>
            </div>
        </div>
    );
};

export default ImportedTranscriptPanel;
